import { useCallback, useEffect, useState } from 'react';
import { errorMessages } from '../consts/errorMsgs.js';
import { callMCCAPI, initCounterData, initFormData, updateCounterData } from '../helpers/FormHelper';

export function useCalculatorState() {
    const formID = "calc-form";

    // For tracking calculation stage [1: for global (G) inputs, 2: for others (OTH), 3: results]
    const [calcStage, setStage] = useState(1);
    const [inputReadOnly, setReadOnly] = useState(false);

    // Input data and results
    const [metadata, setMetadata] = useState({});                       // updated for saved calculation sessions (reference ID, report and contact details)
    const [inputData, setInputData] = useState({});                     // updated on every input change, and after stage 1 calculation
    const [outputData, setOutputData] = useState({});                   // updated after stage 2 calculation, holds calculation results
    const [inputCarbonOffset, setInputCarbonOffset] = useState(0);      // updated in stage 3
    
    // Temporary data
    const [placeholderData, setPlaceholderData] = useState({});         // used after stage 1 calculation, holds guiding values (for placeholders)
    
    // Footprint categories selected by user
    const [selectedFootprintCategories, setFootprintCategories] = useState({
        onsite: true, // default checked
        transport: false,
        accommodations: false,
    });

    // Validation and counter mismatch tracking
    // For tracking data that requires additional checks (e.g. count cannot exceed upper bound)
    const [counterData, setCounterData] = useState({});
    const [errors, setErrors] = useState({});

    // UI flags
    const [isLoading, setIsLoading] = useState(false);
    const [openCounterCheck, setCounterCheckOpen] = useState(false);
    const [openError, setOpenError] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // Helpers
    const checkCounterDataPass = useCallback(() => {
        return !Object.values(counterData).some(d => d.isMismatched);
    }, [counterData]);

    const validateInputs = () => {
        var form = document.getElementById(formID);
        if (!form) return false;

        let inputs  = form.querySelectorAll("input, select");
        return Array.from(inputs).every(input => input.checkValidity());
    }

    // Submission input composition
    const buildSubmitData = useCallback((toSave=false) => {
        return {
            formInputs: {
                ...Object.assign({}, ...Object.values(inputData)),          // Flattens the inputData object to remove tabs
                selected: selectedFootprintCategories,
            },
            calcStage,
            metadata,
            toSave,
            carbonOffset: parseFloat(inputCarbonOffset) || 0
        };
    }, [inputData, selectedFootprintCategories, calcStage, metadata, inputCarbonOffset]);

    // Handle pre-submission validation and user confirmation
    const handleSubmissionPrep = (toSave = false) => {
        if (!validateInputs()) {
            setErrorMsg(errorMessages['MissingGlobal']);
            setOpenError(true);
            return;
        }

        if (Object.keys(errors).length > 0) {
            const errTypes = new Set(Object.values(errors));
            const errMsg = Array.from(errTypes).map(type => errorMessages[type]).join("\n");
            setErrorMsg(errMsg);
            setOpenError(true);
            return;
        }

        const confirmed = window.confirm("Are you sure you want to submit?");
        if (!confirmed) return;

        setIsLoading(true);
        const payload = {
            httpMethod: 'POST',
            ...buildSubmitData(toSave),
        };

        submitToAPI(payload);
    };

    // Handle API submission
    const submitToAPI = (payload) => {
        callMCCAPI('POST', payload).then((data) => {
            const body = JSON.parse(data?.body || '{}');

            if (data.status === "success") {
                const newInputs = body?.inputs;
                setInputData(newInputs);
                setPlaceholderData(newInputs);

                if (calcStage === 1) {
                    setStage(2);
                } else {
                    setStage(3);
                    setMetadata(body?.metadata);
                    setOutputData(body?.outputs);
                }
            } else {
                setErrorMsg(
                    process.env.NODE_ENV === 'development'
                        ? body.message
                        : "Something went wrong. Please try again."
                );
                setOpenError(true);
            }
        }).finally(() => {
            setIsLoading(false);
        });
    };

    // Update counter data whenever form data changes
    useEffect(() => {
        try {
            updateCounterData(inputData, counterData, setCounterData, selectedFootprintCategories);
        } catch (e) {
            console.warn('Failed to update counter data:', e);
        }
    }, [inputData, selectedFootprintCategories]);

    // Initialisation
    useEffect(() => {
        if (calcStage === 1) {
            initFormData(setInputData);
        }
        if (calcStage === 3) {
            setReadOnly(true);
        }
        try {
            initCounterData(inputData, setCounterData, selectedFootprintCategories);
        } catch (e) {
            console.warn('Failed to init counter data:', e);
        }
    }, [calcStage]);

    useEffect(() => {
        setStage(1);
    }, [])

    return {
        formID,
        calcStage, setStage,
        metadata, setMetadata,
        inputData, setInputData,
        placeholderData, setPlaceholderData,
        outputData, setOutputData,
        inputCarbonOffset, setInputCarbonOffset,
        selectedFootprintCategories, setFootprintCategories,
        counterData, setCounterData,
        openCounterCheck, setCounterCheckOpen,
        errors, setErrors,
        openError, setOpenError,
        inputReadOnly, setReadOnly,
        isLoading,
        errorMsg,
        handleSubmissionPrep,
        checkCounterDataPass,
    };
}
